import React, { useState } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Card,
  InputAdornment,
  IconButton,
  Divider,
  alpha,
  Link,
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  LoginOutlined,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { styled } from '@mui/material/styles';
import logo from '../assets/Facteio nouveau Logo.png';

// Styles similaires à Landing.js
const LoginSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: theme.palette.background.default,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 50%, ${alpha(theme.palette.secondary.main, 0.18)} 0%, transparent 55%),
          radial-gradient(circle at 80% 80%, ${alpha(theme.palette.primary.main, 0.14)} 0%, transparent 55%)`,
    animation: 'pulse 8s ease-in-out infinite',
  },
  
  '@keyframes pulse': {
    '0%, 100%': { 
      opacity: 0.5,
      transform: 'scale(1)'
    },
    '50%': { 
      opacity: 0.8,
      transform: 'scale(1.1)'
    },
  },
}));

const ModernCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(253, 251, 248, 0.98) 0%, rgba(240, 232, 223, 0.96) 100%)',
  backdropFilter: 'blur(30px)',
  border: `2px solid ${alpha(theme.palette.primary.main, 0.32)}`,
  borderRadius: 32,
  overflow: 'visible',
  boxShadow: `0 30px 80px ${alpha(theme.palette.secondary.main, 0.22)}, 
              0 0 60px ${alpha(theme.palette.primary.main, 0.16)},
              inset 0 1px 0 ${alpha('#fff', 0.6)}`,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at top right, ${alpha(theme.palette.primary.main, 0.16)} 0%, transparent 60%),
          radial-gradient(circle at bottom left, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 60%)`,
    borderRadius: 32,
    pointerEvents: 'none',
  },
  
  '&:hover': {
    border: `2px solid ${alpha(theme.palette.primary.main, 0.54)}`,
    boxShadow: `0 35px 100px ${alpha(theme.palette.secondary.main, 0.3)}, 
                0 0 80px ${alpha(theme.palette.primary.main, 0.22)},
                inset 0 1px 0 ${alpha('#fff', 0.7)}`,
    transform: 'translateY(-4px)',
  },
}));

const LogoBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  
  '& img': {
    maxWidth: '220px',
    width: '100%',
    height: 'auto',
    filter: 'drop-shadow(0px 10px 40px rgba(94, 86, 79, 0.28))',
  },

  [theme.breakpoints.down('sm')]: {
    '& img': {
      maxWidth: '180px',
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: 'linear-gradient(135deg, rgba(255, 252, 248, 0.95) 0%, rgba(244, 236, 228, 0.9) 100%)',
    backdropFilter: 'blur(10px)',
    borderRadius: 16,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: `inset 0 2px 4px ${alpha('#5E564F', 0.12)}`,
    
    '& fieldset': {
      borderColor: alpha('#977F6B', 0.45),
      borderWidth: 2,
    },
    
    '&:hover fieldset': {
      borderColor: alpha('#977F6B', 0.8),
      boxShadow: `0 0 20px ${alpha('#977F6B', 0.25)}`,
    },
    
    '&.Mui-focused': {
      background: 'linear-gradient(135deg, rgba(255, 252, 248, 0.98) 0%, rgba(242, 231, 220, 0.94) 100%)',
      boxShadow: `0 0 30px ${alpha('#977F6B', 0.35)}, inset 0 2px 4px ${alpha('#5E564F', 0.16)}`,
      
      '& fieldset': {
        borderColor: '#977F6B',
        borderWidth: 2,
      },
    },
  },
  
  '& .MuiInputLabel-root': {
    color: alpha('#4C433D', 0.78),
    fontWeight: 500,
    
    '&.Mui-focused': {
      color: '#8A7563',
      fontWeight: 600,
    },
  },
  
  '& .MuiInputBase-input': {
    color: '#4C433D',
    fontSize: '1rem',
    padding: '16px 14px',
  },
  
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#8A7563',
    filter: 'drop-shadow(0px 0px 8px rgba(151, 127, 107, 0.25))',
  },
}));

const GlowButton = styled(Button)(({ theme, variant: buttonVariant }) => ({
  borderRadius: 16,
  padding: '16px 40px',
  fontSize: '1.1rem',
  fontWeight: 700,
  textTransform: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  letterSpacing: '0.5px',
  
  ...(buttonVariant === 'contained' && {
    background: 'linear-gradient(135deg, #977F6B 0%, #8A7563 50%, #5E564F 100%)',
    backgroundSize: '200% 200%',
    color: '#ffffff',
    boxShadow: `0 10px 40px ${alpha('#5E564F', 0.4)}, 
                0 0 20px ${alpha('#977F6B', 0.32)}`,
    border: `1px solid ${alpha('#977F6B', 0.35)}`,
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      transition: 'left 0.5s',
    },
    
    '&:hover': {
      background: 'linear-gradient(135deg, #A78F7B 0%, #8A7563 50%, #685E56 100%)',
      backgroundSize: '200% 200%',
      boxShadow: `0 15px 60px ${alpha('#5E564F', 0.55)}, 
                  0 0 40px ${alpha('#977F6B', 0.42)},
                  inset 0 1px 0 ${alpha('#fff', 0.2)}`,
      transform: 'translateY(-3px) scale(1.02)',
      
      '&::before': {
        left: '100%',
      },
    },
    
    '&:active': {
      transform: 'translateY(-1px) scale(0.98)',
    },
    
    '&:disabled': {
      background: alpha('#5E564F', 0.35),
      color: alpha('#ffffff', 0.4),
      boxShadow: 'none',
    },
  }),
  
  ...(buttonVariant === 'outlined' && {
    border: `2px solid ${alpha('#977F6B', 0.68)}`,
    color: '#5E564F',
    background: alpha('#977F6B', 0.08),
    
    '&:hover': {
      border: `2px solid #8A7563`,
      background: alpha('#977F6B', 0.18),
      boxShadow: `0 8px 30px ${alpha('#5E564F', 0.22)}`,
      transform: 'translateY(-2px)',
    },
  }),
  
  ...(buttonVariant === 'text' && {
    color: '#8A7563',
    fontWeight: 600,
    
    '&:hover': {
      background: alpha('#977F6B', 0.18),
      color: '#5E564F',
    },
  }),
}));

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (requiresTwoFactor) {
        if (!twoFactorCode) {
          setError('Veuillez saisir votre code 2FA');
          setIsLoading(false);
          return;
        }

        await authService.verifyTwoFactorLogin(twoFactorToken, twoFactorCode);
      } else {
        if (!formData.email || !formData.password) {
          setError('Veuillez remplir tous les champs');
          setIsLoading(false);
          return;
        }

        const loginResponse = await authService.login(formData.email, formData.password);
        if (loginResponse.requiresTwoFactor) {
          setRequiresTwoFactor(true);
          setTwoFactorToken(loginResponse.twoFactorToken || '');
          setError(null);
          setIsLoading(false);
          return;
        }
      }

      onLogin();
      navigate('/dashboard');
    } catch (err) {
      console.error('Erreur de connexion:', err);
      const status = err.response?.status;
      const apiError = err.response?.data?.error || err.response?.data?.message;

      if (requiresTwoFactor && status === 401) {
        setError(apiError || 'Challenge 2FA expiré ou invalide. Recommencez la connexion.');
        setRequiresTwoFactor(false);
        setTwoFactorToken('');
        setTwoFactorCode('');
      } else if (status === 400) {
        if (requiresTwoFactor) {
          setError(apiError || 'Code 2FA invalide');
        } else if ((apiError || '').toLowerCase().includes('email ou mot de passe incorrect')) {
          setError('Email ou mot de passe incorrect');
        } else {
          setError(apiError || 'Informations de connexion invalides');
        }
      } else if (status === 404) {
        setError('Aucun compte trouvé. Veuillez créer un compte en cliquant sur "Créer un compte".');
      } else if (status === 401) {
        setError(apiError || 'Connexion expirée. Recommencez la connexion.');
      } else {
        setError('Erreur de connexion. Vérifiez que le serveur est démarré.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleDemoLogin = async () => {
    setIsLoading(true);
    setError(null);

    const demoCredentials = [
      { email: 'admin@sg-digital.fr', password: 'Admin123!' },
      { email: 'admin@sg-digital.fr', password: 'admin123' },
      { email: 'admin@facteio.fr', password: 'Admin2026!' }
    ];

    try {
      let loggedIn = false;

      for (const creds of demoCredentials) {
        try {
          const response = await authService.login(creds.email, creds.password);
          if (!response?.token) {
            continue;
          }
          setRequiresTwoFactor(false);
          setTwoFactorToken('');
          setTwoFactorCode('');
          setFormData(creds);
          loggedIn = true;
          break;
        } catch (error) {
          // Essayer l'identifiant démo suivant
        }
      }

      if (!loggedIn) {
        const timestamp = Date.now();
        await authService.register({
          email: `demo.${timestamp}@facteio.local`,
          password: 'DemoFacteio2026!',
          name: 'Compte Démo'
        });
      }

      onLogin();
      // Afficher le tutoriel aussi après une connexion démo
      if (localStorage.getItem('onboardingTutorialSeen') !== '1') {
        localStorage.setItem('onboardingTutorialPending', '1');
      }
      navigate('/dashboard');
    } catch (error) {
      setError('Connexion démo indisponible. Vérifiez le compte admin de démonstration.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginSection>
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2, px: { xs: 2, sm: 0 } }}>
        {/* Bouton retour */}
        <Box sx={{ mb: 3 }}>
          <GlowButton
            variant="text"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
              sx={{ color: '#5E564F', '&:hover': { background: alpha('#977F6B', 0.2) } }}
          >
            Retour à l'accueil
          </GlowButton>
        </Box>

        <ModernCard>
          {/* Logo et Titre */}
          <Box sx={{ p: { xs: 3, sm: 5 }, pb: { xs: 2, sm: 3 } }}>
            <LogoBox>
              <img src={logo} alt="Facteio" />
            </LogoBox>

            <Typography 
              variant="h4" 
              align="center" 
              sx={{ 
                color: '#5E564F',
                fontWeight: 700,
                mb: 1,
                textShadow: '0 1px 4px rgba(94,86,79,0.2)'
              }}
            >
              Bienvenue
            </Typography>
            
            <Typography 
              variant="body1" 
              align="center" 
              sx={{ 
                color: alpha('#5E564F', 0.75),
                mb: 4
              }}
            >
              {requiresTwoFactor ? 'Saisissez votre code de vérification 2FA' : 'Connectez-vous à votre compte'}
            </Typography>
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  background: alpha('#d32f2f', 0.1),
                  border: `1px solid ${alpha('#d32f2f', 0.3)}`,
                  color: '#ff6b6b',
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: '#ff6b6b'
                  }
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              {!requiresTwoFactor && (
                <>
                  <StyledTextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <StyledTextField
                    fullWidth
                    label="Mot de passe"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="current-password"
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                            sx={{ color: alpha('#8A6E57', 0.9) }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ textAlign: 'right', mb: 3 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => navigate('/forgot-password')}
                      sx={{
                        color: alpha('#8A6E57', 0.95),
                        textDecoration: 'none',
                        '&:hover': {
                          color: '#5E564F',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Mot de passe oublié ?
                    </Link>
                  </Box>
                </>
              )}

              {requiresTwoFactor && (
                <>
                  <StyledTextField
                    fullWidth
                    label="Code 2FA ou code de secours"
                    name="twoFactorCode"
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                    required
                    autoComplete="one-time-code"
                    sx={{ mb: 2 }}
                    helperText="Exemple: 123456 ou ABCDE-FGHIJ"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      )
                    }}
                  />

                  <Box sx={{ textAlign: 'right', mb: 3 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => {
                        setRequiresTwoFactor(false);
                        setTwoFactorToken('');
                        setTwoFactorCode('');
                        setError(null);
                      }}
                      sx={{
                        color: alpha('#8A6E57', 0.95),
                        textDecoration: 'none',
                        '&:hover': {
                          color: '#5E564F',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Revenir aux identifiants
                    </Link>
                  </Box>
                </>
              )}

              <GlowButton
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                startIcon={<LoginOutlined />}
                sx={{ mb: 3 }}
              >
                {isLoading ? 'Connexion en cours...' : (requiresTwoFactor ? 'Vérifier le code' : 'Se connecter')}
              </GlowButton>
            </Box>

            <Divider sx={{ 
              my: 3,
              '&::before, &::after': {
                borderColor: alpha('#b88333be', 0.35)
              }
            }}>
              <Typography variant="body2" sx={{ color: alpha('#5E564F', 0.6) }}>
                ou
              </Typography>
            </Divider>

            <GlowButton
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => navigate('/register')}
              sx={{ mb: 2 }}
            >
              Créer un compte
            </GlowButton>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <GlowButton
                variant="text"
                size="small"
                onClick={handleDemoLogin}
                disabled={isLoading}
                sx={{ fontSize: '0.875rem' }}
              >
                Connexion démo
              </GlowButton>
            </Box>
          </Box>
        </ModernCard>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: alpha('#5E564F', 0.65),
              textShadow: '0 1px 3px rgba(94,86,79,0.2)'
            }}
          >
            © 2026 SG DIGITAL - Tous droits réservés
          </Typography>
        </Box>
      </Container>
    </LoginSection>
  );
};

export default Login;